import { PlusOutlined } from '@ant-design/icons'
import { Alert, Empty, Select, Skeleton, Tag } from 'antd'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useWorkspaceState } from '../WorkspaceContext'
import CategoriesTab from './CategoriesTab'
import ContentModelsTab from './ContentModelsTab'
import ContentTab from './ContentTab'
import DesignTab from './DesignTab'
import EntryManagerTab from './EntryManagerTab'
import AdminItemDetailPage from './AdminItemDetailPage'
import ItemsTab from './ItemsTab'
import ItemEditorPage from './ItemEditorPage'
import GraphicPagesTab from './graphicPages/GraphicPagesTab'
import GraphicPageEditorPage from './graphicPages/GraphicPageEditorPage'
import PagesTab from './PagesTab'
import PreviewTab from './PreviewTab'
import PublishTab from './PublishTab'
import TopicsTab from './TopicsTab'

/**
 * LiangCollection UI Fidelity (A1) §1/§2/§3 — this page no longer renders
 * its own secondary sidebar (that gray second column has been removed
 * entirely). The "网站" module's navigation now lives directly under
 * Vistoera's ONE primary Sider (see `WorkspaceLayout.tsx` + `websiteNav.ts`
 * for the shared source of truth on group/label/order). This page is now
 * just: a compact site-switcher top bar (spec §3 — "紧凑方式", never a
 * second 200px+ column) + the real routed sub-pages at full content width
 * (spec §4).
 */
export default function WebsiteSitePage() {
  const { siteId = '' } = useParams()
  const { workspace, sites, loading, error } = useWorkspaceState()
  const navigate = useNavigate()
  const location = useLocation()
  const site = sites.find((s) => s.id === siteId) ?? null

  if (error) {
    return <Alert type="error" message={error.message} showIcon style={{ margin: 24 }} />
  }
  if (loading) {
    return <Skeleton active paragraph={{ rows: 6 }} />
  }
  if (!site || !workspace) {
    return <Empty description="该网站在当前工作区中不存在" style={{ marginTop: 80 }} />
  }

  const workspaceId = workspace.id
  const siteIdResolved = site.id
  const base = `/workspace/${workspaceId}/website/${siteIdResolved}`

  const onSwitchSite = (nextSiteId: string) => {
    if (nextSiteId === '__create__') {
      navigate(`/workspace/${workspaceId}/website`)
      return
    }
    // Preserve the current sub-page (items/categories/...) when switching
    // sites — spec §2 "网站切换仍然可以工作".
    const currentPath = location.pathname.slice(base.length)
    navigate(`/workspace/${workspaceId}/website/${nextSiteId}${currentPath}`)
  }

  return (
    <div className="tc-website-page">
      <div className="tc-site-switcher" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Select
          value={siteIdResolved}
          onChange={onSwitchSite}
          style={{ minWidth: 200 }}
          options={[
            ...sites.map((s) => ({ label: s.name, value: s.id })),
            { label: <span><PlusOutlined /> 新建网站</span>, value: '__create__' },
          ]}
        />
        <Tag color={site.status === 'active' ? 'green' : 'default'}>{site.status === 'active' ? '已启用' : site.status}</Tag>
        <span style={{ color: 'var(--text-muted, var(--text-tertiary))', fontSize: 12 }}>/{site.slug}</span>
      </div>
      <Routes>
        <Route index element={<Navigate to="items" replace />} />
        <Route
          path="items"
          element={
                <ItemsTab
                  workspaceId={workspaceId}
                  siteId={siteIdResolved}
                  workspaceSlug={workspace.slug}
                  siteSlug={site.slug}
                  siteBase={base}
                />
          }
        />
        <Route
          path="items/new"
          element={<ItemEditorPage workspaceId={workspaceId} siteId={siteIdResolved} siteBase={base} />}
        />
        <Route
          path="items/:itemId/edit"
          element={<ItemEditorPage workspaceId={workspaceId} siteId={siteIdResolved} siteBase={base} />}
        />
        <Route
          path="items/:itemId"
          element={
            <AdminItemDetailPage
              workspaceId={workspaceId}
              siteId={siteIdResolved}
              workspaceSlug={workspace.slug}
              siteSlug={site.slug}
              siteBase={base}
            />
          }
        />
        <Route path="categories" element={<CategoriesTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        <Route path="topics" element={<TopicsTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        <Route
          path="exhibitions"
          element={
            <EntryManagerTab
              workspaceId={workspaceId}
              siteId={siteIdResolved}
              contentType="exhibition"
              entityLabel="展览"
              entityLabelPlural="展览"
              withItemPicker
              fields={[
                { key: 'description', label: '简介', type: 'textarea' },
                { key: 'venue', label: '地点' },
                { key: 'start_date', label: '开始日期' },
                { key: 'end_date', label: '结束日期' },
              ]}
            />
          }
        />
        <Route
          path="research"
          element={
            <EntryManagerTab
              workspaceId={workspaceId}
              siteId={siteIdResolved}
              contentType="research"
              entityLabel="研究"
              entityLabelPlural="研究"
              withItemPicker
              fields={[
                { key: 'description', label: '摘要', type: 'textarea' },
                { key: 'author', label: '作者' },
              ]}
            />
          }
        />
        <Route path="graphic-pages" element={<GraphicPagesTab workspaceId={workspaceId} siteId={siteIdResolved} siteBase={base} />} />
        <Route
          path="graphic-pages/:pageId/edit"
          element={<GraphicPageEditorPage workspaceId={workspaceId} siteId={siteIdResolved} siteBase={base} />}
        />
        <Route path="design" element={<DesignTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        <Route path="preview" element={<PreviewTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        <Route path="publish" element={<PublishTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        {/* Hidden from the primary nav (spec §4) but still real,
            directly-openable routes for backward compatibility with any
            existing link. `pages` (the old form-based Homepage editor)
            joined this list in the EXISTING_PAGE_EDITOR_INTEGRATION round
            — "首页" now opens in the canonical editor via `graphic-pages`
            instead (see websiteNav.ts), but this route is kept working in
            case anything still links to it directly. */}
        <Route path="pages" element={<PagesTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        <Route path="content-models" element={<ContentModelsTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        <Route path="content" element={<ContentTab workspaceId={workspaceId} siteId={siteIdResolved} />} />
        <Route path="*" element={<Navigate to="items" replace />} />
      </Routes>
    </div>
  )
}
